import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { fetchCostCenters } from '../forms/forms.apis';
import {
  fetchActivityAnalyticsByActivity,
  fetchActivityAnalyticsByType,
  fetchActivityAnalyticsSummary,
  fetchActivityTypes,
} from './activity.apis';
import { ILookupItem } from '../forms/forms.models';
import {
  IActivityAnalyticsByActivityItem,
  IActivityAnalyticsByTypeItem,
  IActivityAnalyticsFilters,
  IActivityAnalyticsSummary,
} from './activity.models';

type Props = {
  navigate?: (path: string) => void;
};

type State = {
  loading: boolean;
  error: string;

  filters: IActivityAnalyticsFilters;
  activityTypes: ILookupItem[];
  costcenters: ILookupItem[];

  summary: IActivityAnalyticsSummary | null;
  byType: IActivityAnalyticsByTypeItem[];
  byActivity: IActivityAnalyticsByActivityItem[];
};

const DEFAULT_FILTERS: IActivityAnalyticsFilters = {
  typeix: '',
  costcenterix: '',
  status: '',
};

const PIE_COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#d32f2f', '#0288d1'];

export default class ActivityAnalyticsPage extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      loading: false,
      error: '',

      filters: { ...DEFAULT_FILTERS },
      activityTypes: [],
      costcenters: [],

      summary: null,
      byType: [],
      byActivity: [],
    };
  }

  componentDidMount(): void {
    this.loadInitialData();
  }

  private loadInitialData = async (): Promise<void> => {
    this.setState({
      loading: true,
      error: '',
    });

    try {
      const [activityTypes, costcenters] = await Promise.all([
        fetchActivityTypes(),
        fetchCostCenters(),
      ]);

      this.setState(
        {
          activityTypes,
          costcenters,
        },
        () => this.loadAnalytics()
      );
    } catch (error: any) {
      this.setState({
        loading: false,
        error: error?.message || 'Failed to load activity analytics lookups',
      });
    }
  };

  private loadAnalytics = async (): Promise<void> => {
    this.setState({
      loading: true,
      error: '',
    });

    try {
      const { filters } = this.state;

      const [summary, byType, byActivity] = await Promise.all([
        fetchActivityAnalyticsSummary(filters),
        fetchActivityAnalyticsByType(filters),
        fetchActivityAnalyticsByActivity(filters),
      ]);

      this.setState({
        summary,
        byType,
        byActivity,
        loading: false,
      });
    } catch (error: any) {
      this.setState({
        loading: false,
        error: error?.message || 'Failed to load activity analytics',
      });
    }
  };

  private handleSelectChange =
    (field: keyof IActivityAnalyticsFilters) =>
    (event: SelectChangeEvent<string>): void => {
      const value = event.target.value;

      this.setState((prevState) => ({
        filters: {
          ...prevState.filters,
          [field]: value,
        },
      }));
    };

  private handleSearch = (): void => {
    this.loadAnalytics();
  };

  private handleReset = (): void => {
    this.setState(
      {
        filters: { ...DEFAULT_FILTERS },
      },
      () => this.loadAnalytics()
    );
  };

  private renderSummaryCard = (
    title: string,
    value: string | number,
    subtitle?: string
  ): React.ReactNode => {
    return (
      <Card>
        <CardContent>
          <Typography variant="caption" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h5" fontWeight={700}>
            {value}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}
        </CardContent>
      </Card>
    );
  };

  private getByTypeChartData(): Array<{ name: string; count: number }> {
    return this.state.byType.map((item) => ({
      name: item.type?.recname || 'Unknown',
      count: item.count,
    }));
  }

  private getByActivityPlannedAmountData(): Array<{ name: string; plannedamount: number }> {
    return this.state.byActivity.map((item) => ({
      name: item.activity?.recname || 'Unknown',
      plannedamount: item.plannedamount || 0,
    }));
  }

  private getByActivityOutputCountData(): Array<{ name: string; outputcount: number }> {
    return this.state.byActivity.map((item) => ({
      name: item.activity?.recname || 'Unknown',
      outputcount: item.outputcount || 0,
    }));
  }

  private getByActivityDriverCountData(): Array<{ name: string; drivercount: number }> {
    return this.state.byActivity.map((item) => ({
      name: item.activity?.recname || 'Unknown',
      drivercount: item.drivercount || 0,
    }));
  }

  render(): React.ReactNode {
    const {
      loading,
      error,
      filters,
      activityTypes,
      costcenters,
      summary,
      byActivity,
    } = this.state;

    const byTypeChartData = this.getByTypeChartData();
    const byActivityPlannedAmountData = this.getByActivityPlannedAmountData();
    const byActivityOutputCountData = this.getByActivityOutputCountData();
    const byActivityDriverCountData = this.getByActivityDriverCountData();

    return (
      <Box sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Activity Analytics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ABB dashboard for activities, planned amounts, drivers, and outputs.
            </Typography>
          </Box>
        </Stack>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel id="activity-analytics-type-label">
                    Activity Type
                  </InputLabel>
                  <Select
                    labelId="activity-analytics-type-label"
                    value={filters.typeix}
                    label="Activity Type"
                    onChange={this.handleSelectChange('typeix')}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {activityTypes.map((item) => (
                      <MenuItem key={item.recix} value={item.recix}>
                        {item.recname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel id="activity-analytics-cc-label">
                    Cost Center
                  </InputLabel>
                  <Select
                    labelId="activity-analytics-cc-label"
                    value={filters.costcenterix}
                    label="Cost Center"
                    onChange={this.handleSelectChange('costcenterix')}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {costcenters.map((item) => (
                      <MenuItem key={item.recix} value={item.recix}>
                        {item.recname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <FormControl fullWidth>
                  <InputLabel id="activity-analytics-status-label">
                    Status
                  </InputLabel>
                  <Select
                    labelId="activity-analytics-status-label"
                    value={filters.status}
                    label="Status"
                    onChange={this.handleSelectChange('status')}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    <MenuItem value="1">Active</MenuItem>
                    <MenuItem value="0">Disabled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={this.handleSearch}>
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={this.handleReset}
                  >
                    Reset
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {loading && !summary ? (
          <Card>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <CircularProgress size={24} />
                <Typography>Loading activity analytics...</Typography>
              </Stack>
            </CardContent>
          </Card>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                {this.renderSummaryCard(
                  'Activity Count',
                  summary?.activitycount || 0
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                {this.renderSummaryCard(
                  'Active Activities',
                  summary?.activeactivitycount || 0
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                {this.renderSummaryCard(
                  'Planned Amount',
                  Number(summary?.totalplannedamount || 0).toLocaleString()
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                {this.renderSummaryCard(
                  'Output Count',
                  summary?.outputcount || 0
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                {this.renderSummaryCard(
                  'Driver Count',
                  summary?.drivercount || 0
                )}
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Activities by Type
                    </Typography>
                    <Box sx={{ width: '100%', height: 320 }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie
                            data={byTypeChartData}
                            dataKey="count"
                            nameKey="name"
                            outerRadius={100}
                            label
                          >
                            {byTypeChartData.map((_entry, index) => (
                              <Cell
                                key={`cell-type-${index}`}
                                fill={PIE_COLORS[index % PIE_COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Planned Amount by Activity
                    </Typography>
                    <Box sx={{ width: '100%', height: 320 }}>
                      <ResponsiveContainer>
                        <BarChart data={byActivityPlannedAmountData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="plannedamount" fill="#1976d2" name="Planned Amount" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Output Count by Activity
                    </Typography>
                    <Box sx={{ width: '100%', height: 320 }}>
                      <ResponsiveContainer>
                        <BarChart data={byActivityOutputCountData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="outputcount" fill="#2e7d32" name="Outputs" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Driver Count by Activity
                    </Typography>
                    <Box sx={{ width: '100%', height: 320 }}>
                      <ResponsiveContainer>
                        <BarChart data={byActivityDriverCountData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="drivercount" fill="#ed6c02" name="Drivers" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Activity Summary
                    </Typography>

                    <Stack spacing={1}>
                      {byActivity.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No activity analytics data available.
                        </Typography>
                      ) : (
                        byActivity.slice(0, 12).map((item, index) => (
                          <Stack
                            key={`${item.activity?.recix || index}`}
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{
                              py: 1,
                              borderBottom:
                                index === byActivity.slice(0, 12).length - 1
                                  ? 'none'
                                  : '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Box>
                              <Typography variant="body2" fontWeight={700}>
                                {item.activity?.recname || 'Unknown'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Outputs: {item.outputcount} | Drivers: {item.drivercount}
                              </Typography>
                            </Box>

                            <Typography variant="body2" fontWeight={700}>
                              {Number(item.plannedamount || 0).toLocaleString()}
                            </Typography>
                          </Stack>
                        ))
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    );
  }
}